import { z } from 'zod';

import { Id, IsoDateTime, Paise } from '../common/primitives';

export { Paise } from '../common/primitives';

// ── Enums ──────────────────────────────────────────────────────────────────

export const PosTerminalStatusSchema = z.enum(['ACTIVE', 'RETIRED']);
export type PosTerminalStatus = z.infer<typeof PosTerminalStatusSchema>;

export const PosShiftStatusSchema = z.enum(['OPEN', 'CLOSED', 'RECONCILED']);
export type PosShiftStatus = z.infer<typeof PosShiftStatusSchema>;

export const PosSaleStatusSchema = z.enum([
  'DRAFT',
  'COMPLETED',
  'PARTIALLY_REFUNDED',
  'REFUNDED',
  'VOIDED',
]);
export type PosSaleStatus = z.infer<typeof PosSaleStatusSchema>;

export const PosPaymentMethodSchema = z.enum(['CASH', 'UPI', 'QR', 'CARD', 'WALLET']);
export type PosPaymentMethod = z.infer<typeof PosPaymentMethodSchema>;

const Pincode = z.string().trim().regex(/^\d{6}$/, 'Pincode must be 6 digits');
const Gstin = z
  .string()
  .trim()
  .regex(/^\d{2}[A-Z]{5}\d{4}[A-Z][A-Z0-9]Z[A-Z0-9]$/, 'Invalid GSTIN format');

// ── Branch + terminal ──────────────────────────────────────────────────────

export const PosBranchSchema = z.object({
  id: Id,
  code: z.string(),
  name: z.string(),
  warehouseId: Id,
  gstin: z.string().nullable(),
  stateCode: z.string(),
  invoicePrefix: z.string(),
  invoiceSequence: z.number().int().nonnegative(),
  active: z.boolean(),
  createdAt: IsoDateTime,
});
export type PosBranch = z.infer<typeof PosBranchSchema>;

export const CreatePosBranchRequest = z
  .object({
    code: z.string().trim().regex(/^[A-Z0-9_-]{2,20}$/, 'Code must be uppercase alphanumeric'),
    name: z.string().trim().min(2).max(120),
    warehouseId: Id,
    gstin: Gstin.optional(),
    stateCode: z.string().trim().length(2),
    invoicePrefix: z.string().trim().regex(/^[A-Z0-9-]{2,12}$/).optional(),
    pincode: Pincode.optional(),
  })
  .strict();
export type CreatePosBranchRequest = z.infer<typeof CreatePosBranchRequest>;

export const PosTerminalSchema = z.object({
  id: Id,
  branchId: Id,
  code: z.string(),
  label: z.string(),
  status: PosTerminalStatusSchema,
  createdAt: IsoDateTime,
});
export type PosTerminal = z.infer<typeof PosTerminalSchema>;

export const CreatePosTerminalRequest = z
  .object({
    branchId: Id,
    code: z.string().trim().regex(/^[A-Z0-9_-]{2,30}$/, 'Code must be uppercase alphanumeric'),
    label: z.string().trim().min(2).max(120),
  })
  .strict();
export type CreatePosTerminalRequest = z.infer<typeof CreatePosTerminalRequest>;

// ── Shift ──────────────────────────────────────────────────────────────────

export const PosShiftSchema = z.object({
  id: Id,
  shiftNumber: z.string(),
  branchId: Id,
  terminalId: Id,
  cashierId: Id,
  status: PosShiftStatusSchema,
  openedAt: IsoDateTime,
  closedAt: IsoDateTime.nullable(),
  openingFloatPaise: Paise,
  closingCashCountedPaise: Paise.nullable(),
  expectedCashPaise: Paise.nullable(),
  variancePaise: z.number().int().nullable(),
  notes: z.string().nullable(),
});
export type PosShift = z.infer<typeof PosShiftSchema>;

export const OpenShiftRequest = z
  .object({
    terminalId: Id,
    openingFloatPaise: Paise.optional(),
    notes: z.string().trim().max(500).optional(),
  })
  .strict();
export type OpenShiftRequest = z.infer<typeof OpenShiftRequest>;

export const CloseShiftRequest = z
  .object({
    closingCashCountedPaise: Paise,
    notes: z.string().trim().max(500).optional(),
  })
  .strict();
export type CloseShiftRequest = z.infer<typeof CloseShiftRequest>;

export const ShiftSummarySchema = z.object({
  shiftId: Id,
  status: PosShiftStatusSchema,
  saleCount: z.number().int().nonnegative(),
  refundCount: z.number().int().nonnegative(),
  grossSalesPaise: Paise,
  refundsPaise: Paise,
  netSalesPaise: Paise,
  cashSalesPaise: Paise,
  cashRefundsPaise: Paise,
  upiSalesPaise: Paise,
  cardSalesPaise: Paise,
  walletSalesPaise: Paise,
  expectedCashPaise: Paise,
  countedCashPaise: Paise.nullable(),
  variancePaise: z.number().int().nullable(),
});
export type ShiftSummary = z.infer<typeof ShiftSummarySchema>;

// ── Sale ──────────────────────────────────────────────────────────────────

const SaleItemInput = z
  .object({
    /// Same shape ERP uses (`sku:...` or `desc:...`). Frontend can send the SKU; helper normalises it.
    itemKey: z.string().trim().min(2).max(120),
    description: z.string().trim().min(1).max(300),
    sku: z.string().trim().max(64).optional(),
    hsnSac: z.string().trim().max(16).optional(),
    qty: z.number().int().positive(),
    /// Selling price the cashier rang up — for variable-priced or marked-down items.
    unitPricePaise: Paise.refine((v) => v > 0),
    gstRate: z.number().int().min(0).max(28).default(0),
    discountPaise: Paise.optional(),
  })
  .strict();

const SalePaymentInput = z
  .object({
    method: PosPaymentMethodSchema,
    amountPaise: Paise.refine((v) => v > 0),
    reference: z.string().trim().max(120).optional(),
    note: z.string().trim().max(200).optional(),
  })
  .strict();

export const CreateSaleRequest = z
  .object({
    shiftId: Id,
    customerId: Id.optional(),
    customerName: z.string().trim().max(120).optional(),
    customerPhone: z.string().trim().max(20).optional(),
    customerGstin: Gstin.optional(),
    /// Buyer state for inter/intra-state GST split. Defaults to the branch's state when omitted.
    buyerStateCode: z.string().trim().length(2).optional(),
    items: z.array(SaleItemInput).min(1).max(200),
    payments: z.array(SalePaymentInput).min(1).max(10),
    notes: z.string().trim().max(500).optional(),
  })
  .strict();
export type CreateSaleRequest = z.infer<typeof CreateSaleRequest>;

export const PosSaleItemSchema = z.object({
  id: Id,
  itemKey: z.string(),
  description: z.string(),
  sku: z.string().nullable(),
  hsnSac: z.string().nullable(),
  qty: z.number().int(),
  unitPricePaise: Paise,
  gstRate: z.number().int(),
  taxPaise: Paise,
  discountPaise: Paise,
  lineSubtotalPaise: Paise,
  refundedQty: z.number().int(),
  cogsPaise: Paise,
});
export type PosSaleItem = z.infer<typeof PosSaleItemSchema>;

export const PosPaymentSchema = z.object({
  id: Id,
  method: PosPaymentMethodSchema,
  amountPaise: Paise,
  reference: z.string().nullable(),
  note: z.string().nullable(),
  createdAt: IsoDateTime,
});
export type PosPayment = z.infer<typeof PosPaymentSchema>;

export const PosSaleSchema = z.object({
  id: Id,
  invoiceNumber: z.string(),
  branchId: Id,
  shiftId: Id,
  cashierId: Id,
  customerId: Id.nullable(),
  status: PosSaleStatusSchema,
  itemsSubtotalPaise: Paise,
  taxPaise: Paise,
  discountPaise: Paise,
  grandTotalPaise: Paise,
  refundedPaise: Paise,
  cogsPaise: Paise,
  intraState: z.boolean(),
  customerName: z.string().nullable(),
  customerPhone: z.string().nullable(),
  customerGstin: z.string().nullable(),
  notes: z.string().nullable(),
  createdAt: IsoDateTime,
  completedAt: IsoDateTime.nullable(),
  refundedAt: IsoDateTime.nullable(),
  voidedAt: IsoDateTime.nullable(),
});
export type PosSale = z.infer<typeof PosSaleSchema>;

export const PosSaleDetailSchema = PosSaleSchema.extend({
  items: z.array(PosSaleItemSchema),
  payments: z.array(PosPaymentSchema),
});
export type PosSaleDetail = z.infer<typeof PosSaleDetailSchema>;

// ── Refunds ───────────────────────────────────────────────────────────────

const RefundLineInput = z
  .object({
    saleItemId: Id,
    qty: z.number().int().positive(),
  })
  .strict();

export const RefundSaleRequest = z
  .object({
    /// Lines to refund. Server validates qty ≤ (sold − already refunded) per line.
    lines: z.array(RefundLineInput).min(1).max(200),
    /// Refund payment method. Defaults to original method mix.
    method: PosPaymentMethodSchema.optional(),
    reason: z.string().trim().min(2).max(500),
    /// Restock the inventory back into the branch. If false, units are written off.
    restock: z.boolean().default(true),
  })
  .strict();
export type RefundSaleRequest = z.infer<typeof RefundSaleRequest>;

// ── GST invoice payload (for printable receipt rendering) ────────────────

export const GstInvoiceLineSchema = z.object({
  description: z.string(),
  hsnSac: z.string().nullable(),
  qty: z.number().int(),
  unitPricePaise: Paise,
  taxablePaise: Paise,
  gstRate: z.number().int(),
  cgstPaise: Paise,
  sgstPaise: Paise,
  igstPaise: Paise,
  lineTotalPaise: Paise,
});
export type GstInvoiceLine = z.infer<typeof GstInvoiceLineSchema>;

export const GstInvoicePayloadSchema = z.object({
  invoiceNumber: z.string(),
  invoiceDate: IsoDateTime,
  branch: z.object({
    code: z.string(),
    name: z.string(),
    gstin: z.string().nullable(),
    stateCode: z.string(),
  }),
  buyer: z.object({
    name: z.string().nullable(),
    phone: z.string().nullable(),
    gstin: z.string().nullable(),
    stateCode: z.string().nullable(),
  }),
  intraState: z.boolean(),
  lines: z.array(GstInvoiceLineSchema),
  totals: z.object({
    taxablePaise: Paise,
    cgstPaise: Paise,
    sgstPaise: Paise,
    igstPaise: Paise,
    discountPaise: Paise,
    grandTotalPaise: Paise,
  }),
  payments: z.array(z.object({
    method: PosPaymentMethodSchema,
    amountPaise: Paise,
    reference: z.string().nullable(),
  })),
});
export type GstInvoicePayload = z.infer<typeof GstInvoicePayloadSchema>;
